import { UserCircleIcon } from '@phosphor-icons/react';
import { useEffect, useMemo, useState } from 'react';
import { getNormalizedSegments, getSegmentsArray } from '../../../utils/comparativeSegmentsHelper';

export default function ComparativeDifficultyTable({
  selectedSubject,
  comparativeDifficultyData,
  userDifficultyData,
  difficultyWiseQuestions,
  type,
  comparativeSegmentsMeta,
}) {
  const [subjectUserDifficultyData, setSubjectUserDifficultyData] = useState(null);
  const [subjectComparativeDifficultyData, setSubjectComparativeDifficultyData] = useState(null);
  const [segments, setSegments] = useState({});

  useEffect(() => {
    const normalizedSegments = getNormalizedSegments(comparativeSegmentsMeta);
    setSegments(normalizedSegments);
  }, [comparativeSegmentsMeta]);

  useEffect(() => {
    const subjectId = selectedSubject?.id?.toString?.();
    const tempUserDifficultyData = userDifficultyData?.find((item) => item?.subject_id?.toString?.() === subjectId);
    const tempComparativeDifficultyData = comparativeDifficultyData?.find(
      (item) => item?.subject_id?.toString?.() === subjectId,
    );

    setSubjectUserDifficultyData(tempUserDifficultyData || null);
    setSubjectComparativeDifficultyData(tempComparativeDifficultyData || null);
  }, [selectedSubject, userDifficultyData, comparativeDifficultyData]);

  const typeData = useMemo(() => {
    return (
      subjectComparativeDifficultyData?.values?.find(
        (item) => item?.title?.toLowerCase?.() === type?.toLowerCase?.(),
      ) || {}
    );
  }, [subjectComparativeDifficultyData, type]);

  const questionCount = difficultyWiseQuestions?.[selectedSubject?.title]?.[type] ?? 0;

  if (!subjectUserDifficultyData || !subjectComparativeDifficultyData) return null;
  if (!segments || Object.keys(segments).length === 0) return null;

  const getSegmentDifficultyValues = (segmentKey) => {
    const values = typeData?.values;
    if (!values) return { correct: 0, incorrect: 0, unattempted: 0 };

    return {
      correct: values?.[`${segmentKey}_correct`] ?? 0,
      incorrect: values?.[`${segmentKey}_incorrect`] ?? 0,
      unattempted: values?.[`${segmentKey}_unattempted`] ?? 0,
    };
  };

  const renderRow = (icon, label, correct, incorrect, unattempted) => (
    <tr className="h-16 border-b border-[var(--q3-stroke-light)] hover:bg-[var(--q3-surface-dim)]">
      <td className="border-r border-[var(--q3-stroke-light)] px-4">
        <div className="flex items-center gap-4 text-[var(--q3-neutral-default)]">
          {icon}
          <p className="truncate text-base font-semibold">{label}</p>
        </div>
      </td>
      <td className="text-center border-r border-[var(--q3-stroke-light)] text-[var(--q3-neutral-default)]">
        {correct}
        <span className="text-xs text-[var(--q3-neutral-light)]">/{questionCount}</span>
      </td>
      <td className="text-center border-r border-[var(--q3-stroke-light)] text-[var(--q3-neutral-default)]">
        {incorrect}
        <span className="text-xs text-[var(--q3-neutral-light)]">/{questionCount}</span>
      </td>
      <td className="text-center text-[var(--q3-neutral-default)]">
        {unattempted}
        <span className="text-xs text-[var(--q3-neutral-light)]">/{questionCount}</span>
      </td>
    </tr>
  );

  const renderComparativeRow = (segment) => {
    const values = getSegmentDifficultyValues(segment.key);
    return (
      <tr key={segment.key} className="h-16 border-b border-[var(--q3-stroke-light)] hover:bg-[var(--q3-surface-dim)]">
        <td className="border-r border-[var(--q3-stroke-light)] px-4">
          <div className="flex items-center gap-4 text-[var(--q3-neutral-default)]">
            <segment.icon size={24} />
            <p className="truncate text-base font-semibold">{segment.title}</p>
          </div>
        </td>
        <td className="text-center border-r border-[var(--q3-stroke-light)] text-[var(--q3-neutral-default)]">
          {values.correct}
          <span className="text-xs text-[var(--q3-neutral-light)]">/{questionCount}</span>
        </td>
        <td className="text-center border-r border-[var(--q3-stroke-light)] text-[var(--q3-neutral-default)]">
          {values.incorrect}
          <span className="text-xs text-[var(--q3-neutral-light)]">/{questionCount}</span>
        </td>
        <td className="text-center text-[var(--q3-neutral-default)]">
          {values.unattempted}
          <span className="text-xs text-[var(--q3-neutral-light)]">/{questionCount}</span>
        </td>
      </tr>
    );
  };

  const comparativeSegmentsArray = getSegmentsArray(segments, 'topper');

  return (
    <div className="overflow-x-auto rounded-b-xl border border-t-0 border-[var(--q3-stroke-light)]">
      <table className="text-sm w-full">
        <thead className="bg-[var(--q3-surface-dim)]">
          <tr className="h-16">
            <td className="py-3 px-4 w-[318px] text-start border-r border-[var(--q3-stroke-light)]">Students</td>
            <td className="py-3 px-4 min-w-[180px] text-center border-r border-[var(--q3-stroke-light)] text-[var(--q3-green-normal)]">
              Attempted Correct
            </td>
            <td className="py-3 px-4 min-w-[180px] text-center border-r border-[var(--q3-stroke-light)] text-[var(--q3-danger-normal)]">
              Attempted Wrong
            </td>
            <td className="py-3 px-4 min-w-[180px] text-center text-[var(--q3-neutral-light)]">Not Attempted</td>
          </tr>
        </thead>
        <tbody>
          {/* You row */}
          {renderRow(
            <UserCircleIcon size={24} />,
            'You',
            subjectUserDifficultyData?.[`${type}Correct`] ?? 0,
            subjectUserDifficultyData?.[`${type}Incorrect`] ?? 0,
            subjectUserDifficultyData?.[`${type}Unattempted`] ?? 0,
          )}

          {comparativeSegmentsArray.map((segment) => renderComparativeRow(segment))}
        </tbody>
      </table>
    </div>
  );
}
